import { Box, Grid } from '@chakra-ui/react';
import { useInfiniteQuery } from '@tanstack/react-query';
import NextLink from 'next/link';
import { FC, useMemo } from 'react';
import { InView } from 'react-intersection-observer';

import { NoContentAvailable } from '@/components/ui/features/events-list/EventListGrid/components/NoContentAvailable';
import { useLibraryFilters } from '@/components/ui/features/library/LibraryFilter/LibraryFilter.hooks';
import { EventCardFallback, PastEventCard, UpcomingEventCard } from '@/components/ui/shared/event/EventCard/EventCard';
import { HubsPageActions } from '@/components/ui/shared/hubs/HubsPageActions/HubsPageActions';
import { eventSortingOptions } from '@/constants/event-sorting-options';
import { EventListStatus } from '@/enums/EventListStatus';
import { EventSort } from '@/enums/EventSort';
import useElement from '@/hooks/useElement';
import { StatusStringParam, StringParam, useQueryParams } from '@/hooks/useQueryParams';
import { thinEventsListInfiniteQueryOptions } from '@/queries/upgrade/thinEvents';
import { getEventHref } from '@/utils/getEventHref';

import { BackToTop } from '../../../core/BackToTop/BackToTop';

interface IUrlQueryStringParams {
  query: string;
  subjectId: string;
  conferenceId: string;
  status: EventListStatus;
  sort: EventSort;
}

export const EventListGrid: FC = () => {
  const { element: eventListElement, ref: eventListRef } = useElement();

  const { subjectId, conferenceId, status, sort, query } = useQueryParams<IUrlQueryStringParams>({
    query: StringParam,
    subjectId: StringParam,
    conferenceId: StringParam,
    status: StatusStringParam,
    sort: StringParam,
  });

  const { filters } = useLibraryFilters();

  const activeStatus = EventListStatus[status] || EventListStatus.live;
  const scope = query ? EventListStatus.unfinished : status || EventListStatus.live;
  const defaultSort = !query && status === EventListStatus.upcoming ? eventSortingOptions[3] : eventSortingOptions[2];
  const actualSort = sort || defaultSort.value;

  const queryFilters = {
    subject_id: subjectId,
    conference_id: conferenceId,
    query,
    scope,
    ...filters,
  };

  const { data, error, fetchNextPage, hasNextPage, isFetchingNextPage } = useInfiniteQuery(
    thinEventsListInfiniteQueryOptions(queryFilters, actualSort)
  );

  const items = useMemo(() => data?.pages.flatMap((page) => page.items) ?? [], [data]);
  const totalCount = data?.pages[0]?.meta?.totalCount;
  const isEmpty = !isFetchingNextPage && items.length === 0;
  const isLoadingMore = isFetchingNextPage;

  return (
    <Box position="relative" ref={eventListRef}>
      <HubsPageActions sortingOptions={eventSortingOptions} defaultOption={defaultSort.value} totalCount={totalCount} />
      <Grid mt={6} templateColumns="repeat(auto-fill, minmax(min(100%, 324px), 1fr))" gap="8">
        {items.map((event) => {
          if (status === EventListStatus.past) {
            return (
              <NextLink key={event.id} href={getEventHref(event)} passHref legacyBehavior>
                <PastEventCard as="a" event={event} />
              </NextLink>
            );
          } else {
            return (
              <NextLink key={event.id} href={getEventHref(event)} passHref legacyBehavior>
                <UpcomingEventCard as="a" event={event} />
              </NextLink>
            );
          }
        })}

        {isLoadingMore ? Array.from({ length: 10 }, (_, i) => <EventCardFallback key={i} />) : null}
      </Grid>

      <InView
        onChange={(inView) => {
          if (inView && !error && hasNextPage) {
            fetchNextPage();
          }
        }}
      >
        <span />
      </InView>

      {isEmpty && <NoContentAvailable activeStatus={activeStatus} query={query} />}

      {!isEmpty && <BackToTop parentTopOffset={eventListElement?.offsetTop} />}
    </Box>
  );
};
